import type { ReactNode } from 'react';

/**
 * The sentence explaining why one field is refused, centred just under it.
 *
 * Two things it has to do, both asked for directly:
 *
 * OUT of the flow, so showing or clearing it never resizes the dialog.
 *
 * And tight to its box — 5px under it, asked for by eye in DevTools. That is
 * why the caller wraps the field in a bare `relative` element and puts the
 * reserved strip on the parent OUTSIDE it:
 * `top: 100%` resolves against the padding box, so a strip on the same element
 * would push the text down by its own height and leave it hanging nearer the
 * next field than its own. Two levels keep the anchor at the input's bottom
 * edge and the reservation where it belongs. Margin cannot do the reserving —
 * it would collapse into the `space-y-*` gap and reserve nothing. [HW-840]
 *
 * It carried a `column` prop for the side-by-side Hours and Minutes boxes,
 * whose two halves each needed their own centre; the linked row replaced that
 * pair with one full-width Duration box, and its time fields refuse nothing —
 * an unreadable time reverts rather than explaining itself — so every message
 * left is centred under a field that spans the dialog. [HW-891]
 */
export function FieldError({ children }: { children: ReactNode }) {
  return (
    <p className="absolute top-full left-1/2 mt-[5px] w-max max-w-[220px] -translate-x-1/2 text-center text-[11px] leading-tight text-destructive">
      {children}
    </p>
  );
}
